// src/redux/sagas/menuItemsSaga.ts
import { call, put, takeEvery, select } from 'redux-saga/effects';
import { request } from '../../api/request';
import ENDPOINTS from '../../api/endpoints';
import {
  fetchMenuItemsRequest,
  fetchMenuItemsSuccess,
  fetchMenuItemsFailure,
} from '../slices/menuItemsSlice';
import { fetchSiteContentSuccess } from '../slices/siteContentSlice';
import { updateWidgetsWithMenuData } from '../../utils/widgetDataUpdater';
import { RootState } from '../store';
import { shouldSkipApiCalls } from '../../utils/appConfig';
import staticMenuData from '../../data/menuData.json'

// API Response interface
interface MenuItemApiResponse {
  id: string;
  name: string;
  price: {
    dining: number;
    online: number;
  };
  description: string | null;
  categoryName: string;
  inventoryStatus: boolean;
  isItemVerified: boolean;
  isDeleted: boolean;
  imageUrl: string | null;
  modifiers: string[];
  posReferenceId?: string; // Add posReferenceId from API
}

// Transform API response to internal MenuItem format
function transformMenuItem(apiItem: MenuItemApiResponse, restaurantId: string): any {
  // Handle null categoryName - use "Uncategorized" as default
  const categoryValue = apiItem.categoryName || 'Uncategorized';
  
  return {
    pk_id: apiItem.id,
    restaurant_id: restaurantId,
    sku_id: apiItem.id,
    name: apiItem.name,
    category: categoryValue,
    description: apiItem.description || '',
    price: apiItem.price, // Keep as object {dining, online}
    large_price: apiItem.price.online.toString(),
    indining_price: apiItem.price.dining.toString(),
    indining_large_price: apiItem.price.dining.toString(),
    image: apiItem.imageUrl || '',
    is_enabled: true, // Always enabled - handle inventory in UI
    is_item_verified: apiItem.isItemVerified,
    product_description: apiItem.description || '',
    level1_category: categoryValue,
    level2_category: '',
    ingredients: [],
    allergens: [],
    food_type: '',
    is_vegan: '',
    modifiers: apiItem.modifiers || [], // Preserve modifiers array from API
    modifiers_list: [],
    party_orders: [],
    inventoryStatus: apiItem.inventoryStatus, // Pass through for UI
    posReferenceId: apiItem.posReferenceId || '', // Add posReferenceId from API
  };
}

function* fetchMenuItemsSaga(): any {
  // Skip API calls if in preview mode
  if (shouldSkipApiCalls()) {
     const staticMenuItems = staticMenuData.data.map((item: MenuItemApiResponse) => transformMenuItem(item, ''));
    yield put(fetchMenuItemsSuccess(staticMenuItems))
    return;
  }

  try {
    // Get selected restaurant from Redux state
    // const selectedRestaurant = yield select((state: RootState) => state.restaurant.selectedRestaurant);
    // selectedRestaurant?.restaurant_id;
    const restaurantId = '7cdb36d9-c51b-473e-9a9f-cfc468ee6d0b'
    
    if (!restaurantId) {
      throw new Error('Restaurant ID is required');
    }
    
    console.log('🍽️ Fetching menu items from NEW API for restaurant:', restaurantId);
    
    // Call API directly
    const response = yield call(request<{
      success: boolean;
      message: string;
      data: MenuItemApiResponse[];
      error: null;
    }>, {
      method: 'GET',
      url: ENDPOINTS.MENU_ITEMS_API_PATH,
      base: ENDPOINTS.API_V2_BASE_URL,
      headers: {
        'restaurant-id': '7cdb36d9-c51b-473e-9a9f-cfc468ee6d0b',
      },
      requiresAuth: false,
      requiresRestaurantId: false,
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch menu items');
    }

    // Transform API response to internal format
    const menuItems = response.data.data.map((item: MenuItemApiResponse) => transformMenuItem(item, restaurantId));
    
    console.log('✅ Menu items fetched successfully:', menuItems.length, 'items');
    
    yield put(fetchMenuItemsSuccess(menuItems));
    
    // Update widgets with menu data AFTER menu data is successfully fetched
    const siteContent = yield select((state: RootState) => state.siteContent.data);
    
    if (siteContent) {
      // Update both widgets and homeWidgets if they exist
      const updatedSiteContent = { ...siteContent };
      
      if (siteContent.widgets) {
        const updatedWidgets = updateWidgetsWithMenuData(siteContent.widgets, menuItems);
        updatedSiteContent.widgets = updatedWidgets;
      }
      
      if (siteContent.homeWidgets) {
        const updatedHomeWidgets = updateWidgetsWithMenuData(siteContent.homeWidgets, menuItems);
        updatedSiteContent.homeWidgets = updatedHomeWidgets;
      }
         
      yield put(fetchSiteContentSuccess(updatedSiteContent as any));
    }
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to fetch menu data';
    console.error('❌ Menu fetch error:', errorMessage);
    yield put(fetchMenuItemsFailure(errorMessage));
  }
}

export function* watchMenuItemsSaga() {
  yield takeEvery(fetchMenuItemsRequest.type, fetchMenuItemsSaga);
}
